import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
  DialogActions,
  AppBar,
  Tabs,
  Tab,
  Box,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { daysUpper } from '../../../utils/parsingUtils';
import { Cell, Partner, PointOfSale } from '@m4a3/api-client';
import { uploadImage } from 'actions/partnerActions';
import { OpeningHourDialogContent } from './OpeningHourDialogContent';
import { OpeningHour } from '@m4a3/api-client/dist/models/shared/opening-hours.interface';
import { StoreInfoDialogContent } from './StoreInfoDialogContent';
import { isValidImage } from '../../../utils/image-utils';
import { useDispatch, useSelector } from 'react-redux';
import { selectCells, selectLandingZones } from '../../../selectors/ordersSelector';
import { logToDatadog } from '../../../services/datadog';
import { getLoggedInUserName } from '../../../services/amplify-auth';
import { getLandingZonesInCell } from '../../../actions/orderActions';

interface StoresDialogProps {
  modalOpen: boolean;
  onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  store?: PointOfSale & { deliveryFee: number, displayName: string };
  edit?: boolean;
  onSave?: (store: PointOfSale) => void;
  onEdit?: (store: PointOfSale) => void;
  notify: (data: { variant: string; message: string }) => void;
  partners: Partner[];
  cellId: number;
  updateOpeningHours: (
    ohData: { openingHoursForWeek: OpeningHour[]; posId: number },
    storeId: number
  ) => void;
}

const openingHoursTemplate = [
  {
    dow: 'SUNDAY',
    startTime: '11:20:00',
    endTime: '17:30:00',
  },
  {
    dow: 'WEDNESDAY',
    startTime: '23:00:00',
    endTime: '23:00:00',
  },
  {
    dow: 'MONDAY',
    startTime: '23:00:00',
    endTime: '23:00:00',
  },
  {
    dow: 'SATURDAY',
    startTime: '11:20:00',
    endTime: '17:30:00',
  },
  {
    dow: 'THURSDAY',
    startTime: '23:00:00',
    endTime: '23:00:00',
  },
  {
    dow: 'FRIDAY',
    startTime: '11:20:00',
    endTime: '17:30:00',
  }
] as unknown as OpeningHour[];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`store-dialog-tabpanel-${index}`}
      aria-labelledby={`store-dialog-tab-${index}`}
      {...other}>
      {value === index && <Box p={1}>{children}</Box>}
    </div>
  );
}

export default function StoresDialog({
  modalOpen,
  onClose,
  store,
  edit,
  onSave,
  onEdit,
  notify,
  partners,
  cellId,
  updateOpeningHours
}: StoresDialogProps) {
  const cells = useSelector(selectCells);
  const dispatch = useDispatch();
  const landingZones = useSelector(selectLandingZones);
  const savingPointOfSale = useSelector((state: any) => state.admin.savingPointOfSale);
  const savingOpeningHours = useSelector((state: any) => state.admin.savingOpeningHours);
  const [posName, setPosName] = useState<string>();
  const [fdAppId, setfdAppId] = useState<number>();
  const [fdMenuId, setfdMenuId] = useState<number>();
  const [preorderAvail, setPreorderAvail] = useState<boolean>();
  const [fdStoreId, setfdStoreId] = useState<number>();
  const [category, setCategory] = useState<string>('');
  const [logo, setLogo] = useState<string>(null);
  const [largeLogo, setLargeLogo] = useState<string>();
  const [landingZoneId, setLandingZoneId] = useState<string>(null);
  const [sortOrder, setOrderInList] = useState<number>();
  const [displayStore, setDisplayStore] = useState<boolean>(false);
  const [openNow, setOpenNow] = useState<boolean>(false);
  const [leadTime, setLeadTime] = useState<number>();
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [image, setImage] = useState<File>(null);
  const [largeImage, setLargeImage] = useState<File>(null);
  const [openingHours, setOpeningHours] = useState<{
    [key: number]: OpeningHour;
  }>();
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState<boolean>(false);
  const [autoPreparingEnabled, setAutoPreparingEnabled] = useState<boolean>(false);
  const [autoPreparedEnabled, setAutoPreparedEnabled] = useState<boolean>(false);
  const [orderHrLimit, setOrderHrLimit] = useState<number>();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [deliveryFeeDisplayValue, setDeliveryFeeDisplayValue] = useState<
    string
  >(''); // we can't use a number here as it doesn't parse partial floats, i.e. '4.' will always resolve to '4' so you cannot type the '.'
  const [uploadedLogoSuccessfully, setUploadedLogoSuccessfully] = useState<
    Boolean
  >(false);
  const [
    uploadedLargeLogoSuccessfully,
    setUploadedLargeLogoSuccessfully
  ] = useState<Boolean>(false);
  const [travelTime, setTravelTime] = useState<number>();
  const [showFdConfirmation, setShowFdConfirmation] = useState(false);
  const isSavingDetailsRef = useRef(false);

  useEffect(() => {
    dispatch(getLandingZonesInCell(cellId));
  }, [cellId]);

  useEffect(() => {
    setUploadedLargeLogoSuccessfully(false);
    setUploadedLogoSuccessfully(false);
  }, [onClose]);

  const useDidMountEffect = (func, deps) => {
    const didMount = useRef(false);

    useEffect(() => {
      if (didMount.current) {
        func();
      } else {
        didMount.current = true;
      }
    }, deps);
  };

  useDidMountEffect(() => {
    if (isValidImage(image)) {
      handleUploadLogo(image, false);
    }
  }, [image]);

  useDidMountEffect(() => {
    if (isValidImage(largeImage)) {
      handleUploadLogo(largeImage, true);
    }
  }, [largeImage]);

  useEffect(() => {
    if (store && edit) {
      setPosName(store.posName ?? store?.displayName);
      setfdAppId(store.fdAppId);
      setfdMenuId(store.fdMenuId);
      setfdStoreId(store.fdStoreId);
      setPartnerId(store.partnerId);
      setLogo(store.logo);
      setLargeLogo(store.largeLogo);
      setOrderInList(store.sortOrder);
      setDisplayStore(store.displayStore);
      setOpenNow(store.open);
      setLeadTime(store.leadTime);
      setPreorderAvail(store.preorderAvail);
      setLandingZoneId(store.landingZoneId);
      setOrderHrLimit(store.orderHrLimit || 0);
      setCategory(store.category);
      setDeliveryFeeDisplayValue(
        store?.deliveryFee === null ? '' : `${store?.deliveryFee}`
      );
      setImage(null);
      setLargeImage(null);
      setTravelTime(store.travelTime);
      setAutoAcceptEnabled(store?.autoAcceptEnabled ?? false);
      setAutoPreparingEnabled(store?.autoPreparingEnabled ?? false);
      setAutoPreparedEnabled(store?.autoPreparedEnabled ?? false);

      if (store.openingHours.length > 0) {
        setOpeningHours(
          store.openingHours
            .sort((a, b) => {
              const aIndex = daysUpper.indexOf(a.dow);
              const bIndex = daysUpper.indexOf(b.dow);
              return aIndex - bIndex;
            }));
      } else {
        setOpeningHours(openingHoursTemplate);
      }
    } else {
      setOpeningHours(openingHoursTemplate);
      setPosName(null);
      setfdAppId(null);
      setfdMenuId(null);
      setfdStoreId(null);
      setPartnerId(null);
      setLogo(null);
      setLargeLogo(null);
      setOrderInList(null);
      setDisplayStore(null);
      setOpenNow(null);
      setLeadTime(null);
      setPreorderAvail(null);
      setOrderHrLimit(0);
      setDeliveryFeeDisplayValue(null);
      setImage(null);
      setLandingZoneId(null);
      setLargeImage(null);
      setTravelTime(null);
      setAutoAcceptEnabled(false);
      setAutoPreparedEnabled(false);
      setAutoPreparedEnabled(false);
    }
  }, [store, onClose]);

  useEffect(() => {
    if (isSavingDetailsRef.current && !savingPointOfSale) {
      isSavingDetailsRef.current = false;
      onClose({}, null);
    }
  }, [savingPointOfSale]);
  
  const handleSwitchAddStore = e => {
    setDisplayStore(e.target.checked);
  };

  const getTimezoneForCell = () => {
    const matchingCell = cells?.find((c: Cell) => c.id === cellId);
    return matchingCell?.timezone;
  }

  const handleUploadLogo = async (image: File, large: Boolean) => {
    try {
      const formData = new FormData();
      formData.append('image', image, image.name);
      const response = await uploadImage(formData, image.name);
      if (large) {
        setLargeLogo(response.fileUrl);
        notify({
          variant: 'success',
          message: 'Successfully uploaded large logo'
        });
      } else {
        setLogo(response.fileUrl);
        notify({
          variant: 'success',
          message: 'Successfully uploaded store logo'
        });
      }
    } catch (err) {
      if (large) {
        notify({
          variant: 'error',
          message: 'Failed to upload large logo ' + err
        });
      } else {
        notify({
          variant: 'error',
          message: 'Failed to upload store logo ' + err
        });
      }
    }
  };

  const handleSave = () => {
    isSavingDetailsRef.current = true;
    if (edit) {
      onEdit(
        Object.assign({}, store, {
          posName,
          partnerId: partnerId,
          cellId,
          displayStore,
          sortOrder,
          logo,
          largeLogo,
          leadTime,
          fdAppId,
          fdMenuId,
          fdStoreId,
          category,
          open: openNow,
          openPreorder: false,
          preorderAvail,
          orderHrLimit,
          landingZoneId,
          travelTime,
          deliveryFee: !isNaN(Number(deliveryFeeDisplayValue))
            ? Number(deliveryFeeDisplayValue)
            : null,
          autoAcceptEnabled,
          autoPreparingEnabled,
          autoPreparedEnabled
        })
      );
    } else {
      onSave(
        Object.assign({}, store, {
          posId: '',
          posName,
          partnerId,
          cellId,
          displayStore,
          sortOrder,
          logo,
          category,
          largeLogo,
          leadTime,
          fdAppId,
          fdMenuId,
          fdStoreId,
          open: openNow,
          openPreorder: false,
          preorderAvail,
          orderHrLimit,
          landingZoneId,
          travelTime,
          deliveryFee: !isNaN(Number(deliveryFeeDisplayValue))
            ? Number(deliveryFeeDisplayValue)
            : null,
          autoAcceptEnabled,
          autoPreparingEnabled,
          autoPreparedEnabled
        })
      );
    }
  };

  const handleSaveOpeningHours = async () => {
    try {
      updateOpeningHours(
        { openingHoursForWeek: Object.values(openingHours), posId: store.id },
        store.id
      );
      const userName = await getLoggedInUserName();
      logToDatadog('Saving opening hours', {
        openingHours: { openingHoursForWeek: Object.values(openingHours), posId: store.id },
        storeName: store.posName,
        timezone: getTimezoneForCell(),
        updatedByUser: userName
      })
      onClose({}, null);
    } catch (e) {
      notify({ variant: 'error', message: 'Failed to update opening hours' });
    }
  };

  const onChangeOpeningHours = (dayOfWeek, hours) => {
    setOpeningHours({
      ...openingHours,
      [dayOfWeek]: hours
    });
  };

  const onChangePartner = evt => {
    setPartnerId(evt.target.value);
  };

  const onChangeOrderThrottling = evt => {
    setOrderHrLimit(Math.abs(parseInt(evt.target.value)) || 0);
  };

  const hasFdFieldsChanged = (): boolean => {
    if (edit && store) {
      return fdAppId !== store.fdAppId ||
        fdMenuId !== store.fdMenuId ||
        fdStoreId !== store.fdStoreId;
    }
    return !!(fdAppId || fdMenuId || fdStoreId);
  };

  const handleSaveWithConfirmation = () => {
    if (hasFdFieldsChanged()) {
      setShowFdConfirmation(true);
    } else {
      handleSave();
    }
  };

  const isValid =
    posName && fdAppId && fdMenuId && logo && largeLogo && sortOrder;

  return (
    <Dialog
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose({}, null);
      }}
      aria-labelledby="customized-dialog-title"
      open={modalOpen}
      classes={{ scrollPaper: 'align-content-normal align-items-normal h-[90%] max-h-[700px] m-0', paper: 'm-[10px_0px] w-full max-w-[500px]' }}>
      <AppBar position="static" sx={{ position: 'relative' }}>
        <Tabs
          value={currentTab}
          textColor={'inherit'}
          onChange={(evt, tab) => {
            setCurrentTab(tab);
          }}>
          <Tab label="Store Details" sx={{ color: 'white' }} />
          <Tab
            label="Opening Hours"
            disabled={!openingHours}
            sx={{ color: 'white' }}
          />
        </Tabs>
        <IconButton
          onClick={() => onClose({}, null)}
          aria-label="Close"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </AppBar>
      <DialogContent dividers>
        <TabPanel value={currentTab} index={0}>
          <StoreInfoDialogContent
            setPosName={setPosName}
            posName={posName}
            fdAppId={fdAppId}
            setfdAppId={setfdAppId}
            fdMenuId={fdMenuId}
            setfdMenuId={setfdMenuId}
            fdStoreId={fdStoreId}
            setfdStoreId={setfdStoreId}
            deliveryFeeDisplayValue={deliveryFeeDisplayValue}
            setDeliveryFeeDisplayValue={setDeliveryFeeDisplayValue}
            setImage={setImage}
            logo={logo}
            isValidImage={isValidImage}
            setLargeImage={setLargeImage}
            largeLogo={largeLogo}
            setOrderInList={setOrderInList}
            sortOrder={sortOrder}
            setLeadTime={setLeadTime}
            leadTime={leadTime}
            onChangeOrderThrottling={onChangeOrderThrottling}
            orderHrLimit={orderHrLimit}
            partnerId={partnerId}
            onChangePartner={onChangePartner}
            partners={partners}
            displayStore={displayStore}
            handleSwitchAddStore={handleSwitchAddStore}
            openNow={openNow}
            setOpenNow={setOpenNow}
            preorderAvail={preorderAvail}
            landingZones={landingZones}
            landingZoneId={landingZoneId}
            setLandingZoneId={setLandingZoneId}
            setPreorderAvail={setPreorderAvail}
            category={category}
            setCategory={setCategory}
            notify={notify}
            autoAcceptEnabled={autoAcceptEnabled}
            setAutoAcceptEnabled={setAutoAcceptEnabled}
            autoPreparingEnabled={autoPreparingEnabled}
            setAutoPreparingEnabled={setAutoPreparingEnabled}
            autoPreparedEnabled={autoPreparedEnabled}
            setAutoPreparedEnabled={setAutoPreparedEnabled}
            travelTime={travelTime}
            setTravelTime={setTravelTime}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          {openingHours && (
            <OpeningHourDialogContent
              store={store}
              openingHours={edit ? store?.openingHours : openingHoursTemplate}
              timezone={getTimezoneForCell()}
              onChangeOpeningHours={onChangeOpeningHours} />
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Grid container>
          <Button
            className="float-right m-[10px]"
            disabled={(currentTab === 0 && !isValid) || savingPointOfSale || savingOpeningHours}
            onClick={currentTab === 0 ? handleSaveWithConfirmation : handleSaveOpeningHours}
            variant="contained"
            color="primary"
            sx={{ minWidth: 140 }}>
            {(currentTab === 0 ? savingPointOfSale : savingOpeningHours)
              ? <CircularProgress size={20} color="inherit" />
              : (currentTab === 0 ? 'Save Details' : 'Save Opening Hours')
            }
          </Button>
        </Grid>
      </DialogActions>

      <Dialog open={showFdConfirmation} onClose={() => setShowFdConfirmation(false)} maxWidth="md" fullWidth>
        <Box sx={{ p: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <h3 style={{ margin: 0, fontFamily: 'Arial', fontSize: 32 }}>Confirm Flipdish IDs</h3>
          </Box>
          <Box sx={{ backgroundColor: '#FFF3CD', border: '1px solid #FFECB5', borderRadius: 2, p: 4, mb: 4 }}>
            <p style={{ margin: 0, fontWeight: 600, color: '#664D03', fontSize: 22 }}>
                ⚠️ Warning: Entering these values incorrectly could result in silently cancelled orders ⚠️
            </p>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2.5, borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 500, fontSize: 22 }}>App ID (fdAppId)</span>
              <span style={{ fontSize: 22 }}>{fdAppId ?? '—'}</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2.5, borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 500, fontSize: 22 }}>Menu ID (fdMenuId)</span>
              <span style={{ fontSize: 22 }}>{fdMenuId ?? '—'}</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 2.5 }}>
              <span style={{ fontWeight: 500, fontSize: 22 }}>Store ID (fdStoreId)</span>
              <span style={{ fontSize: 22 }}>{fdStoreId ?? '—'}</span>
            </Box>
          </Box>
          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button onClick={() => setShowFdConfirmation(false)} color="inherit" size="large" sx={{ fontSize: 18 }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowFdConfirmation(false);
                handleSave();
              }}
              variant="contained"
              color="error"
              size="large"
              sx={{ fontSize: 18, px: 4, py: 1.5 }}>
              Confirm & Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Dialog>
  );
}
